import { LEAD_CONFIRMATION_MESSAGE } from '../../../backend/prompts/conversation-policy.js';
import {
  BOOKING_OTP_CODE_LENGTH as OTP_CODE_LENGTH,
  formatBookingPhone,
  formatOtpSendError,
  formatOtpVerifyError,
  normalizeBookingPhone,
  sendBookingOtpSms,
  verifyBookingOtpCode,
} from './bookingOtp.js';
import { createOtpFunnelActionId, emitOtpFunnelClientEvent } from './otpFunnelTelemetry.js';

const OTP_OVERLAY_ID = 'glov-unified-booking-otp-overlay';
const OTP_STYLE_ID = 'glov-unified-booking-otp-style';

const OTP_OPENED_EVENT = 'glov-booking-otp:opened';
export const OTP_PHONE_CHANGE_EVENT = 'glov-booking-otp:phone-change';
export const OTP_CLOSED_EVENT = 'glov-booking-otp:closed';
export const OTP_VERIFIED_EVENT = 'glov-booking-otp:verified';

type OtpPhase = 'ready' | 'sending' | 'code_entry' | 'verifying' | 'success';

type UnifiedBookingOtpSource = 'booking' | 'voice' | 'text-lead';

export type OpenUnifiedBookingOtpFlowOptions = {
  inlineTarget?: HTMLElement;
  autoSend?: boolean;
  initialPhone?: string | null;
  source?: UnifiedBookingOtpSource;
  threadId?: string;
  bookingCycleId?: number | null;
  correlationId?: string | null;
  readyDescription?: string;
  closeOnVerified?: boolean;
  onVerified?: (phone: string, verificationReceipt?: string) => void;
  onClose?: (reason: 'dismiss' | 'verified') => void;
};

type WebOtpCredentialLike = {
  code?: string;
};

type WebOtpCredentialsContainer = {
  get?: (options: { otp: { transport: Array<'sms'> }; signal?: AbortSignal }) => Promise<unknown>;
};

declare global {
  interface Window {
    glovOpenUnifiedBookingOtpFlow?: (options?: OpenUnifiedBookingOtpFlowOptions) => void;
    glovCloseUnifiedBookingOtpFlow?: () => void;
  }
}

let activeCleanup: (() => void) | null = null;

function emitOtpEvent(eventName: string, detail: Record<string, unknown>) {
  document.dispatchEvent(
    new CustomEvent(eventName, {
      bubbles: true,
      detail,
    })
  );
}

function ensureOtpStyles() {
  if (document.getElementById(OTP_STYLE_ID)) {
    return;
  }

  const style = document.createElement('style');
  style.id = OTP_STYLE_ID;
  style.textContent = `
    #${OTP_OVERLAY_ID}[data-inline='true'] {
      position: relative;
      inset: auto;
      display: block;
      padding: 0;
      background: transparent;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      z-index: auto;
    }
    [data-inline='true'] .glov-booking-otp-sheet {
      position: relative;
      width: 100%;
      max-width: none;
      max-height: none;
      margin: 0;
      border-radius: 14px;
      box-shadow: none;
      overflow: visible;
    }
    [data-inline='true'] .glov-booking-otp-eyebrow,
    [data-inline='true'] .glov-booking-otp-body {
      display: none;
    }
    [data-inline='true']:not([data-phase='success']) .glov-booking-otp-title {
      display: none;
    }
    [data-inline='true'] .glov-booking-otp-title {
      margin: 0 0 10px;
      font-size: 20px;
      line-height: 1.2;
    }
    [data-inline='true'] .glov-booking-otp-topbar {
      position: absolute;
      top: 8px;
      right: 8px;
      z-index: 2;
      justify-content: flex-end;
      min-height: 32px;
      padding: 0;
      border-bottom: 0;
      pointer-events: none;
    }
    [data-inline='true'] .glov-booking-otp-back {
      display: none;
    }
    [data-inline='true'] .glov-booking-otp-close {
      width: 30px;
      height: 30px;
      color: #8b96a8;
      background: #eef2f7;
      font-size: 20px;
      box-shadow: 0 4px 12px rgba(23,32,51,0.14);
      pointer-events: auto;
    }
    [data-inline='true'] .glov-booking-otp-close:hover {
      background: #e4e9f1;
    }
    [data-inline='true'] .glov-booking-otp-content {
      padding: 0;
    }
    [data-inline='true'] .glov-booking-otp-card {
      margin: 0;
      padding: 12px;
      border-radius: 14px;
      gap: 8px;
      background: rgba(255,255,255,0.96);
    }
    [data-inline='true'] .glov-booking-otp-phone-row {
      display: block;
    }
    [data-inline='true'] .glov-booking-otp-icon,
    [data-inline='true'] .glov-booking-otp-field-label {
      display: none;
    }
    [data-inline='true'] .glov-booking-otp-field-wrap {
      display: block;
    }
    [data-inline='true'] .glov-booking-otp-input {
      min-height: 46px;
      border-radius: 14px;
      font-size: 18px;
    }
    [data-inline='true'] .glov-booking-otp-static-phone {
      min-height: 0;
      margin: 0 0 6px;
      font-size: 14px;
    }
    [data-inline='true'] .glov-booking-otp-code-row {
      margin-top: 10px;
      justify-content: center;
      gap: 8px;
    }
    [data-inline='true'] .glov-booking-otp-code-input {
      width: 100%;
      min-width: 0;
      height: 42px;
      border-radius: 12px;
      font-size: 18px;
    }
    [data-inline='true'] .glov-booking-otp-primary {
      margin-top: 10px;
      min-height: 44px;
      padding: 10px 14px;
      border-radius: 14px;
      font-size: 15px;
    }
    [data-inline='true'] .glov-booking-otp-inline-actions {
      margin-top: 6px;
    }
    [data-inline='true'] .glov-booking-otp-error,
    [data-inline='true'] .glov-booking-otp-note {
      margin: 6px 0 0;
      font-size: 12px;
    }
    [data-inline='true'] .glov-booking-otp-note {
      display: none;
    }
    #${OTP_OVERLAY_ID} {
      position: fixed;
      inset: 0;
      /*
       * assistant-fe >=0.7.14 raises its open mobile host to the maximum
       * z-index. This overlay is appended later under document.body, so using
       * the same maximum keeps the OTP sheet above that host and clickable.
       */
      z-index: 2147483647;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      background: rgba(15, 23, 42, 0.30);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    }
    #${OTP_OVERLAY_ID} * {
      box-sizing: border-box;
      font-family: inherit;
    }
    .glov-booking-otp-sheet {
      width: min(100%, 560px);
      max-height: min(92vh, 760px);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      border: 1px solid #e7ebf3;
      border-radius: 30px;
      background: #ffffff;
      box-shadow: 0 24px 64px rgba(23, 32, 51, 0.20);
    }
    .glov-booking-otp-topbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
      padding: 16px 20px;
      border-bottom: 1px solid #edf1f7;
    }
    .glov-booking-otp-back,
    .glov-booking-otp-close,
    .glov-booking-otp-link {
      border: 0;
      background: transparent;
      color: #5f6d84;
      cursor: pointer;
    }
    .glov-booking-otp-back {
      border-radius: 999px;
      padding: 10px 14px;
      font-size: 13px;
      font-weight: 700;
    }
    .glov-booking-otp-close {
      width: 40px;
      height: 40px;
      border-radius: 999px;
      font-size: 24px;
      line-height: 1;
    }
    .glov-booking-otp-back:hover,
    .glov-booking-otp-close:hover,
    .glov-booking-otp-link:hover {
      background: #f6f8fc;
    }
    .glov-booking-otp-content {
      overflow-y: auto;
      padding: 28px 22px 22px;
    }
    .glov-booking-otp-eyebrow {
      margin: 0;
      color: var(--client-primary, #f97d1a);
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0.16em;
      text-transform: uppercase;
    }
    .glov-booking-otp-title {
      margin: 10px 0 0;
      color: #172033;
      font-size: 30px;
      line-height: 1.15;
      font-weight: 700;
    }
    .glov-booking-otp-body {
      margin: 12px 0 0;
      color: #5f6d84;
      font-size: 15px;
      line-height: 1.65;
    }
    .glov-booking-otp-card {
      margin-top: 24px;
      border: 1px solid #edf1f7;
      border-radius: 24px;
      background: #f8fafc;
      padding: 20px;
    }
    .glov-booking-otp-phone-row {
      display: grid;
      grid-template-columns: 48px minmax(0, 1fr);
      column-gap: 14px;
      row-gap: 8px;
      align-items: center;
    }
    .glov-booking-otp-icon {
      grid-column: 1;
      grid-row: 1 / span 2;
      align-self: center;
      flex: 0 0 48px;
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 18px;
      background: #ffffff;
      color: var(--client-primary, #f97d1a);
      box-shadow: 0 8px 18px rgba(23, 32, 51, 0.06);
      font-size: 22px;
    }
    .glov-booking-otp-icon svg {
      width: 22px;
      height: 22px;
      display: block;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
    .glov-booking-otp-field-label {
      grid-column: 2;
      grid-row: 1;
      margin: 0;
      color: #7a879b;
      font-size: 12px;
      font-weight: 600;
    }
    .glov-booking-otp-field-wrap {
      grid-column: 2;
      grid-row: 2;
    }
    .glov-booking-otp-input {
      width: 100%;
      border: 1px solid #d9dee8;
      border-radius: 18px;
      background: #ffffff;
      padding: 14px 16px;
      color: #172033;
      font-size: 18px;
      font-weight: 700;
      outline: none;
    }
    .glov-booking-otp-input:focus {
      border-color: var(--client-primary, #f97d1a);
    }
    .glov-booking-otp-static-phone {
      color: #172033;
      font-size: 18px;
      font-weight: 700;
      min-height: 52px;
      display: flex;
      align-items: center;
    }
    .glov-booking-otp-primary {
      width: 100%;
      margin-top: 18px;
      border: 0;
      border-radius: 18px;
      background: var(--client-primary, #f97d1a);
      color: #ffffff;
      padding: 15px 18px;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
    }
    .glov-booking-otp-primary[disabled] {
      cursor: default;
      opacity: 0.75;
    }
    .glov-booking-otp-code-row {
      display: grid;
      grid-template-columns: repeat(${OTP_CODE_LENGTH}, minmax(0, 1fr));
      gap: 10px;
      margin-top: 18px;
    }
    .glov-booking-otp-code-input {
      width: 100%;
      border: 1px solid #d9dee8;
      border-radius: 18px;
      background: #ffffff;
      padding: 14px 0;
      color: #172033;
      font-size: 24px;
      font-weight: 800;
      line-height: 1;
      text-align: center;
      outline: none;
    }
    .glov-booking-otp-code-input:focus {
      border-color: var(--client-primary, #f97d1a);
    }
    .glov-booking-otp-inline-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 12px;
      margin-top: 14px;
    }
    .glov-booking-otp-link {
      border-radius: 999px;
      padding: 8px 12px;
      font-size: 13px;
      font-weight: 700;
    }
    .glov-booking-otp-error {
      min-height: 20px;
      margin: 12px 0 0;
      color: #c53f3f;
      font-size: 13px;
      line-height: 1.5;
    }
    .glov-booking-otp-note {
      margin: 14px 0 0;
      color: #7a879b;
      font-size: 13px;
      line-height: 1.55;
    }
    .glov-booking-otp-success-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      border-radius: 18px;
      background: rgba(249, 125, 26, 0.12);
      color: var(--client-primary, #f97d1a);
      font-size: 24px;
      font-weight: 700;
      box-shadow: inset 0 0 0 1px rgba(249, 125, 26, 0.12);
    }
    @media (max-width: 768px) {
      #${OTP_OVERLAY_ID} {
        padding: 0;
      }
      .glov-booking-otp-sheet {
        width: 100%;
        height: 100%;
        max-height: none;
        border-radius: 0;
      }
      .glov-booking-otp-topbar {
        padding: 14px 16px;
      }
      .glov-booking-otp-content {
        padding: 24px 16px 20px;
      }
      .glov-booking-otp-title {
        font-size: 26px;
      }
      .glov-booking-otp-card {
        padding: 18px 16px;
      }
    }
  `;
  document.head.appendChild(style);
}

function createButton(text: string, className: string, onClick: () => void): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.textContent = text;
  button.addEventListener('click', onClick);
  return button;
}

function installGhostTapBlocker(): void {
  const blocker = (delayedEvent: Event) => {
    delayedEvent.preventDefault();
    delayedEvent.stopPropagation();
    delayedEvent.stopImmediatePropagation();
  };
  const clickOptions: AddEventListenerOptions = { capture: true };
  const touchOptions: AddEventListenerOptions = { capture: true, passive: false };
  document.addEventListener('click', blocker, clickOptions);
  document.addEventListener('touchend', blocker, touchOptions);
  window.setTimeout(() => {
    document.removeEventListener('click', blocker, clickOptions);
    document.removeEventListener('touchend', blocker, touchOptions);
  }, 480);
}

function bindGhostTapSafeDismiss(target: HTMLElement, onDismiss: () => void): void {
  target.addEventListener(
    'touchend',
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      installGhostTapBlocker();
      onDismiss();
    },
    { passive: false }
  );
}

function getOtpIconMarkup(kind: 'phone' | 'success'): string {
  if (kind === 'success') {
    return `
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M20 7L10.5 16.5L7 13" />
      </svg>
    `;
  }

  return `
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M7 3H17C18.1 3 19 3.9 19 5V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V5C5 3.9 5.9 3 7 3Z" />
      <path d="M11 17H13" />
      <path d="M9 6H15" />
    </svg>
  `;
}

function formatEditablePhone(raw: string): string {
  const digits = raw.replace(/[^\d]/g, '').slice(0, 12);
  const normalized =
    digits.length === 12 && digits.startsWith('90')
      ? `0${digits.slice(2)}`
      : digits.length === 10 && digits.startsWith('5')
        ? `0${digits}`
        : digits.slice(0, 11);

  const groups = [
    normalized.slice(0, 4),
    normalized.slice(4, 7),
    normalized.slice(7, 9),
    normalized.slice(9, 11),
  ].filter(Boolean);
  return groups.join(' ');
}

export function closeUnifiedBookingOtpFlow(): void {
  activeCleanup?.();
}

export function openUnifiedBookingOtpFlow(options: OpenUnifiedBookingOtpFlowOptions = {}): void {
  const {
    initialPhone = '',
    source = 'booking',
    threadId,
    bookingCycleId,
    correlationId,
    readyDescription,
    closeOnVerified = false,
    onClose,
    onVerified,
  } = options;
  const inlineTarget = options.inlineTarget ?? null;

  closeUnifiedBookingOtpFlow();
  ensureOtpStyles();

  const overlay = document.createElement('div');
  overlay.id = OTP_OVERLAY_ID;

  const sheet = document.createElement('div');
  sheet.className = 'glov-booking-otp-sheet';

  const topbar = document.createElement('div');
  topbar.className = 'glov-booking-otp-topbar';

  const backButton = createButton('Rezervasyona dön', 'glov-booking-otp-back', () =>
    close('dismiss')
  );
  const closeButton = createButton('×', 'glov-booking-otp-close', () => close('dismiss'));
  closeButton.setAttribute('aria-label', 'Kapat');
  bindGhostTapSafeDismiss(backButton, () => close('dismiss'));
  bindGhostTapSafeDismiss(closeButton, () => close('dismiss'));

  topbar.appendChild(backButton);
  topbar.appendChild(closeButton);

  const content = document.createElement('div');
  content.className = 'glov-booking-otp-content';

  const eyebrow = document.createElement('p');
  eyebrow.className = 'glov-booking-otp-eyebrow';
  eyebrow.textContent = 'SMS Doğrulama';

  const title = document.createElement('h2');
  title.className = 'glov-booking-otp-title';

  const description = document.createElement('p');
  description.className = 'glov-booking-otp-body';

  const card = document.createElement('div');
  card.className = 'glov-booking-otp-card';

  const phoneRow = document.createElement('div');
  phoneRow.className = 'glov-booking-otp-phone-row';

  const icon = document.createElement('div');
  icon.className = 'glov-booking-otp-icon';
  icon.innerHTML = getOtpIconMarkup('phone');

  const fieldWrap = document.createElement('div');
  fieldWrap.className = 'glov-booking-otp-field-wrap';

  const fieldLabel = document.createElement('p');
  fieldLabel.className = 'glov-booking-otp-field-label';
  fieldLabel.textContent = 'Telefon numarası';

  const phoneInput = document.createElement('input');
  phoneInput.className = 'glov-booking-otp-input';
  phoneInput.type = 'tel';
  phoneInput.inputMode = 'tel';
  phoneInput.placeholder = '05xx xxx xx xx';
  phoneInput.value = formatEditablePhone(initialPhone ?? '');

  const staticPhone = document.createElement('p');
  staticPhone.className = 'glov-booking-otp-static-phone';

  fieldWrap.appendChild(phoneInput);
  phoneRow.appendChild(icon);
  phoneRow.appendChild(fieldLabel);
  phoneRow.appendChild(fieldWrap);

  const primaryButton = createButton('SMS Gönder', 'glov-booking-otp-primary', () => {
    void handlePrimaryAction();
  });

  const codeRow = document.createElement('div');
  codeRow.className = 'glov-booking-otp-code-row';

  const codeInputs: HTMLInputElement[] = [];
  const codeDigits = Array(OTP_CODE_LENGTH).fill('');

  for (let index = 0; index < OTP_CODE_LENGTH; index++) {
    const input = document.createElement('input');
    input.className = 'glov-booking-otp-code-input';
    input.type = 'text';
    input.inputMode = 'numeric';
    input.maxLength = index === 0 ? OTP_CODE_LENGTH : 1;
    input.autocomplete = index === 0 ? 'one-time-code' : 'off';
    input.setAttribute('aria-label', `Doğrulama kodu ${index + 1}`);
    input.addEventListener('input', () => {
      const sanitized = input.value.replace(/[^\d]/g, '').slice(0, OTP_CODE_LENGTH);
      if (sanitized.length > 1) {
        applyOtpCode(sanitized, { autoVerify: true });
        return;
      }
      input.value = sanitized;
      codeDigits[index] = sanitized;
      clearError();
      if (sanitized && index < OTP_CODE_LENGTH - 1) {
        codeInputs[index + 1]?.focus();
      }
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace' && !input.value && index > 0) {
        codeInputs[index - 1]?.focus();
      }
    });
    input.addEventListener('paste', (event) => {
      const pasted = (event.clipboardData?.getData('text') ?? '')
        .replace(/[^\d]/g, '')
        .slice(0, OTP_CODE_LENGTH);

      if (!pasted) {
        return;
      }

      event.preventDefault();
      applyOtpCode(pasted, { autoVerify: true });
    });
    codeInputs.push(input);
    codeRow.appendChild(input);
  }

  const inlineActions = document.createElement('div');
  inlineActions.className = 'glov-booking-otp-inline-actions';

  const editPhoneButton = createButton('Numarayı düzenle', 'glov-booking-otp-link', () => {
    phase = 'ready';
    syncUi();
  });

  const resendCodeButton = createButton('Kodu tekrar gönder', 'glov-booking-otp-link', () => {
    void sendSms();
  });

  inlineActions.appendChild(editPhoneButton);
  inlineActions.appendChild(resendCodeButton);

  const errorText = document.createElement('p');
  errorText.className = 'glov-booking-otp-error';
  errorText.setAttribute('aria-live', 'polite');

  const noteText = document.createElement('p');
  noteText.className = 'glov-booking-otp-note';

  card.appendChild(phoneRow);
  card.appendChild(primaryButton);
  card.appendChild(errorText);
  card.appendChild(noteText);

  content.appendChild(eyebrow);
  content.appendChild(title);
  content.appendChild(description);
  content.appendChild(card);

  sheet.appendChild(topbar);
  sheet.appendChild(content);
  overlay.appendChild(sheet);
  if (inlineTarget) {
    overlay.dataset.inline = 'true';
  }
  (inlineTarget ?? document.body).appendChild(overlay);
  const previousBodyOverflow = document.body.style.overflow;
  if (!inlineTarget) {
    document.body.style.overflow = 'hidden';
  }

  let phase: OtpPhase = 'ready';
  let verifiedPhone: string | null = null;
  let isClosed = false;
  let sendInFlight = false;
  let verificationInFlight = false;
  let otpLeadId: string | undefined;
  let autoVerifyTimerId: number | null = null;
  let webOtpAbortController: AbortController | null = null;

  const onEscape = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      close('dismiss');
    }
  };

  function clearAutoVerifyTimer() {
    if (autoVerifyTimerId !== null) {
      window.clearTimeout(autoVerifyTimerId);
      autoVerifyTimerId = null;
    }
  }

  function cancelWebOtpRequest() {
    webOtpAbortController?.abort();
    webOtpAbortController = null;
  }

  function applyOtpCode(code: string, options: { autoVerify?: boolean } = {}) {
    const digits = code.replace(/[^\d]/g, '').slice(0, OTP_CODE_LENGTH);
    for (let digitIndex = 0; digitIndex < OTP_CODE_LENGTH; digitIndex++) {
      const nextValue = digits[digitIndex] || '';
      codeDigits[digitIndex] = nextValue;
      codeInputs[digitIndex].value = nextValue;
    }

    const lastFilledIndex = Math.max(0, Math.min(digits.length, OTP_CODE_LENGTH) - 1);
    codeInputs[lastFilledIndex]?.focus();
    clearError();

    if (options.autoVerify && digits.length === OTP_CODE_LENGTH) {
      scheduleAutoVerify();
    }
  }

  function scheduleAutoVerify() {
    if (
      phase !== 'code_entry' ||
      verificationInFlight ||
      codeDigits.join('').length !== OTP_CODE_LENGTH
    ) {
      return;
    }

    clearAutoVerifyTimer();
    autoVerifyTimerId = window.setTimeout(() => {
      autoVerifyTimerId = null;
      if (
        isClosed ||
        phase !== 'code_entry' ||
        verificationInFlight ||
        codeDigits.join('').length !== OTP_CODE_LENGTH
      ) {
        return;
      }
      void verifyCode();
    }, 0);
  }

  async function startWebOtpAutocomplete() {
    cancelWebOtpRequest();
    if (
      phase !== 'code_entry' ||
      typeof window === 'undefined' ||
      typeof navigator === 'undefined' ||
      window.isSecureContext !== true ||
      typeof AbortController !== 'function'
    ) {
      return;
    }

    const credentials = navigator.credentials as WebOtpCredentialsContainer | undefined;
    if (typeof credentials?.get !== 'function') {
      return;
    }

    const controller = new AbortController();
    webOtpAbortController = controller;
    try {
      const credential = (await credentials.get({
        otp: { transport: ['sms'] },
        signal: controller.signal,
      })) as WebOtpCredentialLike | null;
      if (
        controller.signal.aborted ||
        isClosed ||
        phase !== 'code_entry' ||
        typeof credential?.code !== 'string'
      ) {
        return;
      }

      applyOtpCode(credential.code, { autoVerify: true });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        // Browser support is intentionally best-effort; manual entry remains primary.
      }
    } finally {
      if (webOtpAbortController === controller) {
        webOtpAbortController = null;
      }
    }
  }

  function cleanupDom() {
    if (isClosed) {
      return;
    }
    isClosed = true;
    clearAutoVerifyTimer();
    cancelWebOtpRequest();
    if (!inlineTarget) {
      document.body.style.overflow = previousBodyOverflow;
    }
    document.removeEventListener('keydown', onEscape);
    overlay.remove();
    activeCleanup = null;
  }

  function emitPhoneChangeIfValid() {
    const normalizedPhone = normalizeBookingPhone(phoneInput.value);
    if (!normalizedPhone) {
      return;
    }
    emitOtpEvent(OTP_PHONE_CHANGE_EVENT, {
      phone: normalizedPhone,
      source,
    });
  }

  function clearError() {
    errorText.textContent = '';
  }

  function setError(message: string) {
    errorText.textContent = message;
  }

  function setInputDisabled(disabled: boolean) {
    phoneInput.disabled = disabled;
    for (const input of codeInputs) {
      input.disabled = disabled;
    }
  }

  function syncUi() {
    const normalizedPhone = normalizeBookingPhone(phoneInput.value);
    overlay.dataset.phase = phase;

    title.textContent =
      phase === 'success' ? 'Talebiniz tamamlandı' : 'Telefon numaranızı doğrulayın';
    description.textContent =
      phase === 'success'
        ? 'Uzman ekibimiz talebinizi aldı. En kısa sürede sizi arayacaktır.'
        : readyDescription ||
          'Talebinizi güvenle iletebilmemiz için bu adımı SMS kodu ile tamamlayın.';

    if (phase === 'success') {
      clearAutoVerifyTimer();
      cancelWebOtpRequest();
      icon.innerHTML = getOtpIconMarkup('success');
      icon.style.background = 'rgba(249, 125, 26, 0.12)';
      fieldWrap.replaceChildren(staticPhone);
      staticPhone.textContent = formatBookingPhone(
        verifiedPhone || normalizedPhone || phoneInput.value
      );
      primaryButton.textContent = 'Tamam';
      primaryButton.disabled = false;
      codeRow.remove();
      inlineActions.remove();
      noteText.textContent = LEAD_CONFIRMATION_MESSAGE;
      setInputDisabled(true);
      return;
    }

    icon.innerHTML = getOtpIconMarkup('phone');
    icon.style.background = '#ffffff';
    fieldWrap.replaceChildren(phoneInput);

    if (phase === 'ready') {
      clearAutoVerifyTimer();
      cancelWebOtpRequest();
      primaryButton.textContent = 'SMS Gönder';
      primaryButton.disabled = false;
      codeDigits.fill('');
      for (const input of codeInputs) {
        input.value = '';
      }
      codeRow.remove();
      inlineActions.remove();
      noteText.textContent = normalizedPhone
        ? `${formatBookingPhone(normalizedPhone)} numarasına doğrulama kodu göndereceğiz.`
        : 'Uzman ekibimizin sizi arayabilmesi için telefon numaranızı doğrulayın.';
      setInputDisabled(false);
      return;
    }

    if (phase === 'sending') {
      clearAutoVerifyTimer();
      cancelWebOtpRequest();
      primaryButton.textContent = 'SMS gönderiliyor...';
      primaryButton.disabled = true;
      codeRow.remove();
      inlineActions.remove();
      noteText.textContent = 'Kısa mesaj gönderilirken lütfen sayfayı kapatmayın.';
      setInputDisabled(true);
      return;
    }

    fieldWrap.replaceChildren(fieldLabel, staticPhone);
    staticPhone.textContent = formatBookingPhone(normalizedPhone || phoneInput.value);
    primaryButton.textContent = phase === 'verifying' ? 'Kod doğrulanıyor...' : 'Kodu Doğrula';
    primaryButton.disabled = phase === 'verifying';
    if (!codeRow.parentElement) {
      card.insertBefore(codeRow, primaryButton);
    }
    if (!inlineActions.parentElement) {
      card.insertBefore(inlineActions, errorText);
    }
    noteText.textContent = `${formatBookingPhone(
      normalizedPhone || phoneInput.value
    )} numarasına gönderilen ${OTP_CODE_LENGTH} haneli kodu girin. Destekleyen telefonlarda kod otomatik doldurulabilir.`;
    setInputDisabled(phase === 'verifying');
    if (phase === 'code_entry') {
      window.setTimeout(() => codeInputs[0]?.focus(), 0);
      return;
    }
    clearAutoVerifyTimer();
    cancelWebOtpRequest();
  }

  function close(reason: 'dismiss' | 'verified') {
    if (isClosed) {
      return;
    }
    // A dismiss after successful verification (X, Escape, outside click on the
    // success screen) must still report 'verified' so listeners never re-show
    // the OTP prompt for an already-verified cycle.
    const effectiveReason = verifiedPhone ? 'verified' : reason;
    emitOtpFunnelClientEvent({
      event: 'overlay',
      outcome: 'closed',
      reason: effectiveReason === 'verified' ? 'verified' : 'dismissed',
      correlationId,
      cycleId: bookingCycleId,
    });
    emitPhoneChangeIfValid();
    emitOtpEvent(OTP_CLOSED_EVENT, { reason: effectiveReason, source });
    onClose?.(effectiveReason);
    cleanupDom();
  }

  async function sendSms() {
    if (sendInFlight) {
      return;
    }

    const normalizedPhone = normalizeBookingPhone(phoneInput.value);

    if (!normalizedPhone) {
      setError('Lütfen geçerli bir telefon numarası girin.');
      return;
    }

    emitOtpEvent(OTP_PHONE_CHANGE_EVENT, {
      phone: normalizedPhone,
      source,
    });
    clearError();
    sendInFlight = true;
    const actionId = createOtpFunnelActionId();
    emitOtpFunnelClientEvent({
      event: 'otp_send_action',
      outcome: 'attempted',
      reason: 'none',
      correlationId,
      cycleId: bookingCycleId,
      actionId,
    });
    phase = 'sending';
    syncUi();

    const result = await sendBookingOtpSms(normalizedPhone, {
      threadId,
      bookingCycleId,
      source,
      actionId,
    }).finally(() => {
      sendInFlight = false;
    });

    if (isClosed) {
      return;
    }

    if (!result.success) {
      if (result.transportError) {
        emitOtpFunnelClientEvent({
          event: 'otp_send_action',
          outcome: 'failed',
          reason: 'network_error',
          correlationId,
          cycleId: bookingCycleId,
          actionId,
        });
      }
      phase = 'ready';
      syncUi();
      setError(
        formatOtpSendError(
          result.statusDescription ||
            result.networkError ||
            'SMS gönderilemedi. Lütfen tekrar deneyin.'
        )
      );
      return;
    }

    otpLeadId = result.otpLeadId;
    phase = 'code_entry';
    syncUi();
    void startWebOtpAutocomplete();
  }

  async function verifyCode() {
    if (verificationInFlight) {
      return;
    }

    const normalizedPhone = normalizeBookingPhone(phoneInput.value);
    const code = codeDigits.join('');

    if (!normalizedPhone) {
      setError('Lütfen geçerli bir telefon numarası girin.');
      return;
    }

    if (code.length !== OTP_CODE_LENGTH) {
      setError(`Lütfen SMS ile gelen ${OTP_CODE_LENGTH} haneli kodu girin.`);
      return;
    }

    clearError();
    clearAutoVerifyTimer();
    cancelWebOtpRequest();
    verificationInFlight = true;
    const actionId = createOtpFunnelActionId();
    emitOtpFunnelClientEvent({
      event: 'otp_verify_action',
      outcome: 'attempted',
      reason: 'none',
      correlationId,
      cycleId: bookingCycleId,
      actionId,
    });
    phase = 'verifying';
    syncUi();

    const result = await verifyBookingOtpCode(normalizedPhone, code, {
      otpLeadId,
      threadId,
      bookingCycleId,
      source,
      actionId,
    }).finally(() => {
      verificationInFlight = false;
    });

    if (isClosed) {
      return;
    }

    if (!result.success) {
      if (result.transportError) {
        emitOtpFunnelClientEvent({
          event: 'otp_verify_action',
          outcome: 'failed',
          reason: 'network_error',
          correlationId,
          cycleId: bookingCycleId,
          actionId,
        });
      }
      phase = 'code_entry';
      syncUi();
      setError(
        formatOtpVerifyError(
          result.statusDescription ||
            result.networkError ||
            'Kod doğrulanamadı. Lütfen tekrar deneyin.'
        )
      );
      return;
    }

    verifiedPhone = normalizedPhone;
    emitOtpEvent(OTP_VERIFIED_EVENT, {
      phone: normalizedPhone,
      source,
      verificationReceipt: result.verificationReceipt,
    });
    onVerified?.(normalizedPhone, result.verificationReceipt);
    if (closeOnVerified) {
      close('verified');
      return;
    }
    phase = 'success';
    syncUi();
  }

  async function handlePrimaryAction() {
    if (phase === 'ready') {
      await sendSms();
      return;
    }

    if (phase === 'code_entry') {
      await verifyCode();
      return;
    }

    if (phase === 'success') {
      close('verified');
    }
  }

  phoneInput.addEventListener('input', () => {
    clearError();
    phoneInput.value = formatEditablePhone(phoneInput.value);
    if (phase !== 'ready') {
      phase = 'ready';
      syncUi();
    }
  });

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) {
      close('dismiss');
    }
  });
  overlay.addEventListener(
    'touchend',
    (event) => {
      if (event.target !== overlay) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      installGhostTapBlocker();
      close('dismiss');
    },
    { passive: false }
  );

  document.addEventListener('keydown', onEscape);

  activeCleanup = () => {
    close('dismiss');
  };

  emitOtpEvent(OTP_OPENED_EVENT, {
    phone: normalizeBookingPhone(phoneInput.value) || phoneInput.value || null,
    source,
  });
  emitOtpFunnelClientEvent({
    event: 'overlay',
    outcome: 'opened',
    reason: 'none',
    correlationId,
    cycleId: bookingCycleId,
  });
  syncUi();
  if (options.autoSend && normalizeBookingPhone(phoneInput.value)) {
    void sendSms();
  }
}

window.glovOpenUnifiedBookingOtpFlow = openUnifiedBookingOtpFlow;
window.glovCloseUnifiedBookingOtpFlow = closeUnifiedBookingOtpFlow;
